import { useMemo, useRef, useState } from "react";

import { StepShell } from "./StepShell";

import {
  CheckCircle2,
  Clock3,
  FileText,
  Loader2,
  Upload,
  AlertTriangle,
  Plus,
  X,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { Button } from "@/components/ui/button";
import portalApi from "@/lib/portalApi";

const STATUS = {
  PENDING: "PENDING",
  PARTIALLY_UPLOADED: "PARTIALLY_UPLOADED",
  UPLOADED: "UPLOADED",
  UNDER_VERIFICATION: "UNDER_VERIFICATION",
  VERIFIED: "VERIFIED",
  REUPLOAD_REQUIRED: "REUPLOAD_REQUIRED",
};

function getStatusLabel(status) {
  switch (status) {
    case STATUS.PARTIALLY_UPLOADED:
      return "Partially Uploaded";

    case STATUS.UPLOADED:
      return "Uploaded";

    case STATUS.UNDER_VERIFICATION:
      return "Under Verification";

    case STATUS.VERIFIED:
      return "Verified";

    case STATUS.REUPLOAD_REQUIRED:
      return "Re-upload Required";

    case STATUS.PENDING:
    default:
      return "Pending";
  }
}

function getStatusIcon(status) {
  switch (status) {
    case STATUS.VERIFIED:
      return CheckCircle2;

    case STATUS.REUPLOAD_REQUIRED:
      return AlertTriangle;

    case STATUS.UPLOADED:
    case STATUS.UNDER_VERIFICATION:
      return Clock3;

    default:
      return FileText;
  }
}

function getApplicantLabel(document) {
  if (document.applicantType === "CO_APPLICANT") {
    return "Co-Applicant";
  }

  if (document.applicantType === "PRIMARY_APPLICANT") {
    return "Primary Applicant";
  }

  return "Application";
}

function groupDocuments(documents) {
  const groups = {};

  for (const document of documents || []) {
    const applicantLabel = getApplicantLabel(document);

    if (!groups[applicantLabel]) {
      groups[applicantLabel] = [];
    }

    groups[applicantLabel].push(document);
  }

  return groups;
}

const getReplacementKey = (documentId, fileId) => `${documentId}-${fileId}`;

export default function DocumentsStep({ token, state, setStep, onUpdated }) {
  const documents = useMemo(
    () => (Array.isArray(state?.documents) ? state.documents : []),
    [state?.documents],
  );

  const progress = state?.document_progress || null;

  const groupedDocuments = useMemo(
    () => groupDocuments(documents),
    [documents],
  );

  /*
   * Currently uploading:
   *
   * {
   *   documentId,
   *   rowIndex
   * }
   */
  const [uploading, setUploading] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    description: "",
  });

  const showAlert = (title, description) => {
    setAlert({
      open: true,
      title,
      description,
    });
  };

  /*
   * Selected file rows.
   *
   * Example:
   *
   * {
   *   123: [File, null, File],
   *   456: [File]
   * }
   *
   * Every row represents ONE upload.
   */
  const [selectedFiles, setSelectedFiles] = useState({});

  const [replacementFiles, setReplacementFiles] = useState({});

  /*
   * File input refs are stored using:
   *
   * `${documentId}-${rowIndex}`
   */
  const fileInputs = useRef({});

  /*
   * --------------------------------------------------------------------------
   * Helpers
   * --------------------------------------------------------------------------
   */

  const getRows = (documentId) => {
    const rows = selectedFiles[documentId];

    /*
     * Always show one upload row initially.
     */
    if (!Array.isArray(rows) || rows.length === 0) {
      return [null];
    }

    return rows;
  };

  const getUploadedCount = (document) =>
    Number(document.uploadedFilesCount || 0);

  const getMaximumAllowed = (document) =>
    Number(document.maximumAllowedFiles || 1);

  const getActiveFiles = (document) =>
    Array.isArray(document.files)
      ? document.files.filter(
          (file) => file?.isActive !== false && file?.isDeleted !== true,
        )
      : [];

  const getActiveFileCount = (document) => getActiveFiles(document).length;

  const getPendingRows = (document) => {
    const rows = selectedFiles[document.id];

    if (!Array.isArray(rows) || rows.length === 0) {
      return [null];
    }

    return rows;
  };

  const canAddRow = (document) => {
    const maximumAllowed = getMaximumAllowed(document);

    const uploadedCount = getActiveFileCount(document);

    const rows = getPendingRows(document);

    return uploadedCount + rows.length < maximumAllowed;
  };

  /*
   * --------------------------------------------------------------------------
   * File selection
   * --------------------------------------------------------------------------
   */

  const handleFileChange = (document, rowIndex, event) => {
    const file = event.target.files?.[0] || null;

    setSelectedFiles((current) => {
      const rows = [...(current[document.id] || [null])];

      rows[rowIndex] = file;

      return {
        ...current,
        [document.id]: rows,
      };
    });
  };

  /*
   * --------------------------------------------------------------------------
   * Add upload row
   * --------------------------------------------------------------------------
   */

  const addUploadRow = (document) => {
    if (!canAddRow(document)) {
      return;
    }

    setSelectedFiles((current) => {
      const rows = [...(current[document.id] || [null])];

      rows.push(null);

      return {
        ...current,
        [document.id]: rows,
      };
    });
  };

  /*
   * --------------------------------------------------------------------------
   * Remove an empty upload row
   * --------------------------------------------------------------------------
   */

  const removeUploadRow = (document, rowIndex) => {
    setSelectedFiles((current) => {
      const rows = [...(current[document.id] || [null])];

      /*
       * Always keep at least one row.
       */
      if (rows.length <= 1) {
        return {
          ...current,
          [document.id]: [null],
        };
      }

      rows.splice(rowIndex, 1);

      return {
        ...current,
        [document.id]: rows,
      };
    });

    const refKey = `${document.id}-${rowIndex}`;

    if (fileInputs.current[refKey]) {
      fileInputs.current[refKey].value = "";
    }
  };

  /*
   * --------------------------------------------------------------------------
   * Upload ONE file
   * --------------------------------------------------------------------------
   */

  const upload = async (document, rowIndex, file) => {
    if (!file) {
      showAlert("No File Selected", "Please select a file before uploading.");
      return;
    }

    const uploadedCount = getActiveFileCount(document);
    const maximumAllowed = getMaximumAllowed(document);

    /*
     * Only maximum is validated here.
     *
     * minimumRequiredFiles must NOT block
     * individual uploads.
     */
    if (uploadedCount >= maximumAllowed) {
      showAlert(
        "Maximum Files Reached",
        `Maximum ${maximumAllowed} file(s) allowed.`,
      );
      return;
    }

    setUploading({
      documentId: document.id,
      rowIndex,
    });

    try {
      const updated = await portalApi.uploadDocument(token, document, [file]);

      setSelectedFiles((current) => {
        const rows = [...(current[document.id] || [])];

        rows.splice(rowIndex, 1);

        return {
          ...current,
          [document.id]: rows,
        };
      });

      const refKey = `${document.id}-${rowIndex}`;

      if (fileInputs.current[refKey]) {
        fileInputs.current[refKey].value = "";
      }

      if (updated) {
        onUpdated?.(updated);
      } else {
        onUpdated?.();
      }
    } catch (error) {
      console.error("Document upload failed:", error);

      showAlert(
        "Document Upload Failed",
        error?.data?.message ||
          error?.message ||
          "Document upload failed. Please try again.",
      );
    } finally {
      setUploading(null);
    }
  };
  const handleReplacementFileChange = (document, documentFile, event) => {
    const file = event.target.files?.[0] || null;

    const key = getReplacementKey(document.id, documentFile.id);

    setReplacementFiles((current) => ({
      ...current,
      [key]: file,
    }));
  };

  const replaceFile = async (document, documentFile) => {
    const key = getReplacementKey(document.id, documentFile.id);

    const file = replacementFiles[key];

    if (!file) {
      return;
    }

    setUploading({
      documentId: document.id,
      fileId: documentFile.id,
      replacement: true,
    });

    try {
      const updated = await portalApi.replaceDocumentFile(
        token,
        document,
        documentFile.id,
        file,
      );

      setReplacementFiles((current) => {
        const next = {
          ...current,
        };

        delete next[key];

        return next;
      });

      const refKey = `replacement-${document.id}-${documentFile.id}`;

      if (fileInputs.current[refKey]) {
        fileInputs.current[refKey].value = "";
      }

      if (updated) {
        onUpdated?.(updated);
      } else {
        onUpdated?.();
      }
    } catch (error) {
      console.error("Document replacement failed:", error);

      showAlert(
        "Document Replacement Failed",
        error?.data?.message ||
          error?.message ||
          "Document replacement failed. Please try again.",
      );
    } finally {
      setUploading(null);
    }
  };

  /*
   * --------------------------------------------------------------------------
   * Continue
   * --------------------------------------------------------------------------
   */

  const uploadPercentage = Math.min(
    100,
    Math.max(0, Number(progress?.uploadPercentage || 0)),
  );

  const canContinue = documents.length === 0 || uploadPercentage >= 100;

  return (
    <StepShell
      testId="portal-documents"
      title="Document Upload"
      subtitle="Select the required file(s), then click the Upload button beside the corresponding document field. After uploading, Money Mart will verify your documents. If any document requires resubmission, the reason and resubmission requirement will be shown here."
      onBack={() => setStep(5)}
      onNext={() => setStep(7)}
      nextLabel="Save & Continue"
      disableNext={!canContinue}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Document Progress                                                    */}
      {/* ------------------------------------------------------------------ */}

      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Document Progress</h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Upload the documents requested for your application.
            </p>
          </div>

          <div className="text-2xl font-semibold">
            {Math.round(uploadPercentage)}%
          </div>
        </div>

        <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{
              width: `${uploadPercentage}%`,
            }}
          />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
          <Stat label="Required" value={progress?.total || 0} />

          <Stat label="Uploaded" value={progress?.uploadedTotal || 0} />

          <Stat label="Verified" value={progress?.verified || 0} />

          <Stat
            label="Re-upload Required"
            value={progress?.reuploadRequired || 0}
          />

          <Stat
            label="Upload Progress"
            value={`${Math.round(uploadPercentage)}%`}
          />
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Documents                                                            */}
      {/* ------------------------------------------------------------------ */}

      {documents.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground" />

          <p className="mt-3 font-medium">Documents are not available yet.</p>

          <p className="mt-1 text-sm text-muted-foreground">
            Please complete the application details first.
          </p>
        </div>
      ) : (
        Object.entries(groupedDocuments).map(
          ([applicantLabel, applicantDocuments]) => (
            <div key={applicantLabel} className="space-y-4">
              <h2 className="text-lg font-semibold">{applicantLabel}</h2>

              {applicantDocuments.map((document) => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  rows={getPendingRows(document)}
                  uploading={uploading}
                  fileInputs={fileInputs}
                  replacementFiles={replacementFiles}
                  onFileChange={handleFileChange}
                  onUpload={upload}
                  onAddRow={addUploadRow}
                  onRemoveRow={removeUploadRow}
                  onReplacementFileChange={handleReplacementFileChange}
                  onReplaceFile={replaceFile}
                  canAddRow={canAddRow(document)}
                />
              ))}
            </div>
          ),
        )
      )}

      <AlertDialog
        open={alert.open}
        onOpenChange={(open) => {
          if (!open) {
            setAlert({
              open: false,
              title: "",
              description: "",
            });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alert.title}</AlertDialogTitle>

            <AlertDialogDescription>{alert.description}</AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StepShell>
  );
}

/*
 * ==========================================================================
 * Document Card
 * ==========================================================================
 */

function DocumentCard({
  document,
  rows,
  uploading,
  fileInputs,
  replacementFiles,
  onFileChange,
  onUpload,
  onAddRow,
  onRemoveRow,
  onReplacementFileChange,
  onReplaceFile,
  canAddRow,
}) {
  const Icon = getStatusIcon(document.status);

  const canUpload =
    document.status === STATUS.PENDING ||
    document.status === STATUS.PARTIALLY_UPLOADED ||
    document.status === STATUS.UPLOADED;

  const maximumAllowed = Number(document.maximumAllowedFiles || 1);

  const uploadedFiles = Array.isArray(document.files)
    ? document.files.filter(
        (file) => file?.isActive !== false && file?.isDeleted !== true,
      )
    : [];

  const canReplaceFile = (documentFile) => {
    return documentFile.verificationStatus === "REJECTED";
  };

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-col gap-5">
        {/* ------------------------------------------------------------ */}
        {/* Header */}
        {/* ------------------------------------------------------------ */}

        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 shrink-0" />

              <h3 className="font-semibold">{document.documentTypeName}</h3>
            </div>

            {document.description && (
              <p className="mt-1 text-sm text-muted-foreground">
                {document.description}
              </p>
            )}
          </div>

          <StatusBadge status={document.status} />
        </div>

        {/* ------------------------------------------------------------ */}
        {/* Information */}
        {/* ------------------------------------------------------------ */}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Info label="Status" value={getStatusLabel(document.status)} />

          <Info label="Required" value={document.minimumRequiredFiles} />

          <Info label="Maximum" value={document.maximumAllowedFiles} />

          <Info label="Uploaded" value={uploadedFiles.length} />
        </div>

        {/* ------------------------------------------------------------ */}
        {/* Existing uploaded files */}
        {/* ------------------------------------------------------------ */}

        {uploadedFiles.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-medium">Uploaded files</div>

            {uploadedFiles.map((documentFile) => {
              const replacementKey = getReplacementKey(
                document.id,
                documentFile.id,
              );

              const replacementFile = replacementFiles[replacementKey] || null;

              const isReplacing =
                uploading?.documentId === document.id &&
                uploading?.fileId === documentFile.id &&
                uploading?.replacement === true;

              return (
                <div
                  key={documentFile.id}
                  className="rounded-xl border bg-muted/20 p-3"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {documentFile.originalName}
                        </p>

                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                          Version {documentFile.version}
                        </span>
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <FileVerificationBadge
                          status={documentFile.verificationStatus}
                        />

                        {documentFile.reuploadReason && (
                          <span className="text-xs text-destructive">
                            {documentFile.reuploadReason}
                          </span>
                        )}
                      </div>
                    </div>

                    {canReplaceFile(documentFile) && (
                      <>
                        <div className="flex min-h-11 min-w-0 flex-1 items-center rounded-xl border border-gray-200 bg-white px-2">
                          <label
                            htmlFor={`replacement-${document.id}-${documentFile.id}`}
                            className="inline-flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[#0F2D52] bg-white px-4 text-sm font-semibold text-[#0F2D52] transition-colors hover:bg-[#0F2D52]/5"
                          >
                            Choose Files
                          </label>

                          <input
                            id={`replacement-${document.id}-${documentFile.id}`}
                            ref={(element) => {
                              fileInputs.current[
                                `replacement-${document.id}-${documentFile.id}`
                              ] = element;
                            }}
                            type="file"
                            multiple={false}
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={(event) =>
                              onReplacementFileChange(
                                document,
                                documentFile,
                                event,
                              )
                            }
                            className="sr-only"
                          />

                          <span className="ml-3 min-w-0 truncate text-sm text-muted-foreground">
                            {replacementFile
                              ? replacementFile.name
                              : "No file selected"}
                          </span>
                        </div>

                        <Button
                          type="button"
                          disabled={isReplacing || !replacementFile}
                          onClick={() => onReplaceFile(document, documentFile)}
                          className="sm:min-w-[110px]"
                        >
                          {isReplacing ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="mr-2 h-4 w-4" />
                          )}
                          Replace
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ------------------------------------------------------------ */}
        {/* New uploads */}
        {/* ------------------------------------------------------------ */}

        {canUpload && (
          <div className="border-t pt-5">
            <div className="space-y-3">
              <label className="text-sm font-medium">Add files</label>

              {rows.map((file, rowIndex) => {
                const isUploading =
                  uploading?.documentId === document.id &&
                  uploading?.rowIndex === rowIndex &&
                  !uploading?.replacement;

                const refKey = `${document.id}-${rowIndex}`;

                return (
                  <div
                    key={refKey}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center"
                  >
                    <div className="flex min-h-11 min-w-0 flex-1 items-center rounded-xl border border-gray-200 bg-white px-2">
                      <label
                        htmlFor={`document-files-${document.id}-${rowIndex}`}
                        className="inline-flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[#0F2D52] bg-white px-4 text-sm font-semibold text-[#0F2D52] transition-colors hover:bg-[#0F2D52]/5"
                      >
                        Choose Files
                      </label>

                      <input
                        id={`document-files-${document.id}-${rowIndex}`}
                        ref={(element) => {
                          fileInputs.current[refKey] = element;
                        }}
                        type="file"
                        multiple={false}
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(event) =>
                          onFileChange(document, rowIndex, event)
                        }
                        className="sr-only"
                      />

                      <span className="ml-3 min-w-0 truncate text-sm text-muted-foreground">
                        {file ? file.name : "No files selected"}
                      </span>
                    </div>

                    <Button
                      type="button"
                      disabled={isUploading || !file}
                      onClick={() => onUpload(document, rowIndex, file)}
                      className="sm:min-w-[110px]"
                    >
                      {isUploading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      Upload
                    </Button>

                    {rows.length > 1 && !file && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemoveRow(document, rowIndex)}
                        className="shrink-0 text-muted-foreground hover:text-red-600"
                        aria-label="Remove upload field"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}

              {/* ------------------------------------------------------ */}
              {/* Add */}
              {/* ------------------------------------------------------ */}

              {canAddRow && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onAddRow(document)}
                  className="mt-1"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              )}

              {/* ------------------------------------------------------ */}
              {/* Capacity */}
              {/* ------------------------------------------------------ */}

              {maximumAllowed > 1 && (
                <p className="text-xs text-muted-foreground">
                  You can upload up to {maximumAllowed} file(s) for this
                  document.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FileVerificationBadge({ status }) {
  switch (status) {
    case "VERIFIED":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Verified
        </span>
      );

    case "REJECTED":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          Re-upload Required
        </span>
      );

    case "PENDING":
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
          <Clock3 className="h-3.5 w-3.5" />
          Under Review
        </span>
      );
  }
}

/*
 * ==========================================================================
 * Status Badge
 * ==========================================================================
 */

function StatusBadge({ status }) {
  const className =
    status === STATUS.VERIFIED
      ? "bg-green-100 text-green-700"
      : status === STATUS.REUPLOAD_REQUIRED
        ? "bg-red-100 text-red-700"
        : status === STATUS.UPLOADED
          ? "bg-blue-100 text-blue-700"
          : "bg-muted text-muted-foreground";

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${className}`}
    >
      {getStatusLabel(status)}
    </span>
  );
}

/*
 * ==========================================================================
 * Info
 * ==========================================================================
 */

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>

      <div className="mt-1 text-sm font-medium">{value ?? "—"}</div>
    </div>
  );
}

/*
 * ==========================================================================
 * Stat
 * ==========================================================================
 */

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>

      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
